/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Box, Checkbox, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { addNewClient, deleteCompany, editClient, getClients } from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { Modal } from "antd";

const Clients = () => {
    const {state} = useAuth();
    const [clients, setClients] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentClient, setCurrentClient] = useState<any>(null);
    const [formData, setFormData] = useState({
        companyName: '',
        status: true
    });
    const [formError, setFormError] = useState('');

    const handleAddClick = () => {
        setModalMode('add');
        setFormData({
            companyName: '',
            status: true
        });
        setCurrentClient(null);
        setFormError('');
        setIsDialogOpen(true);
    };

    const handleEditClick = (params: any) => {
        setModalMode('edit');
        setCurrentClient(params.row);
        setFormData({
            companyName: params.row.companyName,
            status: params.row.status
        });
        setFormError('');
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setFormData({
            companyName: '',
            status: true
        });
        setCurrentClient(null);
        setFormError('');
    };

    const handleSubmit = async () => {
        if (!formData.companyName.trim()) {
            setFormError('Client name cannot be empty');
            return;
        }

        if (modalMode === 'add') {
            const response = await addNewClient(state.user?.token, formData.companyName);
            if (response) {
                toast.success(response.data);
                getAllClients();
                handleDialogClose();
            } else {
                toast.error('Something went wrong!');
            }
        } else {
            if (currentClient) {
                const response = await editClient(state.user?.token, {
                    id: currentClient.id,
                    companyName: formData.companyName,
                    status: formData.status
                });
                if (response === 403) {
                    toast.warn('You are not authorized!');
                } else if (response) {
                    toast.success(response.data);
                    getAllClients();
                    handleDialogClose();
                }
            }
        }
    };

    const handleDelete = async (id: any) => {
        try {
            const response: any = await deleteCompany(state.user?.token, id);
            if(response?.forbidden) {
                toast.warn('You don\'t have permission to delete this client');
                return;
            }
        } catch (error) {
            console.error("Failed to fetch clients:", error);
        } finally {
            getAllClients();
        }
    };

    const getAllClients = async () => {
        try {
            setLoading(true);
            const response = await getClients(state.user?.token);
            if (response) {
                setClients(response);
            }
        } catch (error) {
            console.error("Failed to fetch clients:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllClients();
    }, []);

    const columns: any = [
        {
            field: "companyName",
            headerName: "Client Name",
            flex: 1,
            renderCell: (params: any) => (
                <div>{params.row.companyName}</div>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.5,
            renderCell: (params: any) => (
                <div style={{color: params.row.status ? 'green' : 'red'}}>
                    {params.row.status ? 'Active' : 'Inactive'}
                </div>
            ),
        },
        {
            field: "createdDate",
            headerName: "Created Date",
            flex: 0.5,
            renderCell: (params: any) => (
                <div>
                    {format(new Date(params.row.createdDate || Date.now()), 'dd/MM/yyyy')}
                </div>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            renderCell: (params: any) => (
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(params)}
                        style={{ marginRight: 8, backgroundColor: "#6c757d", boxShadow: "none" }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        style={{boxShadow: "none"}}
                        onClick={() => 
                            Modal.confirm({
                                title: `Are you sure you want to delete ${params.row.companyName}?`,
                                onOk: () => handleDelete(params.row.id),
                                okButtonProps: { danger: true },
                                okText: 'YES',
                                cancelText: 'NO',
                                centered: true
                            })
                        }
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <Box
            sx={{ 
                p: 1,
                height: '100%',
                minHeight: '100vh'
            }}>
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 3, 
                    borderRadius: '16px',
                    backgroundColor: '#ffffff',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}
            >
                <Box
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        alignItems: 'center',
                        mb: 3 
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleAddClick}
                        style={{boxShadow: 'none'}}
                    >
                        New Client
                    </Button>
                </Box>
                <Box
                    sx={{
                        width: "100%",
                        height: '740px',
                        margin: "0 auto",
                        "@media (max-width: 800px)": {
                            overflowX: "auto",
                            margin: 1,
                            minWidth: "95%",
                        },
                    }}
                >
                    <DataGrid
                        rows={clients}
                        columns={columns}
                        disableRowSelectionOnClick
                        loading={loading}
                        sx={{
                            "@media (max-width: 700px)": {
                                minWidth: "100%",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                color: "#333",
                                fontWeight: "bold",
                                fontSize: 15,
                                backgroundColor: '#f0f0f0',
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f1f1f1",
                            },
                        }}
                    />
                </Box>
            </Paper>

            <Dialog 
                open={isDialogOpen} 
                onClose={handleDialogClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {modalMode === 'add' ? 'Add New Client' : 'Edit Client'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Client Name"
                        type="text"
                        size="small"
                        fullWidth
                        variant="outlined"
                        value={formData.companyName}
                        onChange={(e) => {
                            setFormData({...formData, companyName: e.target.value});
                            setFormError('');
                        }}
                        error={!!formError}
                        helperText={formError}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSubmit();
                            }
                        }}
                        sx={{ mt: 2, mb: 2 }}
                    />
                    
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.status}
                                onChange={(e) => setFormData({...formData, status: e.target.checked})}
                                color="primary"
                            />
                        }
                        label="Active Status"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" style={{boxShadow: 'none'}}>
                        {modalMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Clients;
